import { useCallback, useEffect, useRef, useState } from 'react';
import type { TokenStats } from '~/lib/.server/rateLimit';
import { getOrganizationRateLimit, getUserRateLimit } from '~/lib/rate-limits';
import type { OrganizationData } from '~/types/organization';
import { SELECTED_ACCOUNT_USER } from '~/utils/constants';

interface RequestRef {
  id: number | 'USER';
  controller: AbortController;
}

export function useTokenStats() {
  const currentRequestRef = useRef<RequestRef | null>(null);

  const [tokenStats, setTokenStats] = useState<TokenStats | undefined>(undefined);
  const [loadingTokenStats, setLoadingTokenStats] = useState(false);
  const [tokenStatsFetchError, setTokenStatsFetchError] = useState(false);

  const cancelPendingRequest = useCallback(() => {
    if (currentRequestRef.current) {
      currentRequestRef.current.controller.abort();
      currentRequestRef.current = null;
    }
  }, []);

  const fetchTokenStats = useCallback(async (account: OrganizationData | 'USER' | null) => {
    cancelPendingRequest();
    setTokenStatsFetchError(false);

    // if a non-paid org or create a team option is selected, no need to fetch token stats
    if (!account || (account !== SELECTED_ACCOUNT_USER && !account.billing)) {
      setTokenStats(undefined);
      setLoadingTokenStats(false);

      return;
    }

    const controller = new AbortController();

    const id = account === SELECTED_ACCOUNT_USER ? account : account.id;

    currentRequestRef.current = { id, controller };

    setLoadingTokenStats(true);

    try {
      const result =
        typeof id === 'number'
          ? await getOrganizationRateLimit(id, { signal: controller.signal })
          : await getUserRateLimit({ signal: controller.signal });

      if (currentRequestRef.current?.id === id) {
        setTokenStats(result);
      }
    } catch (error) {
      if (currentRequestRef.current?.id === id && !(error instanceof DOMException && error.name === 'AbortError')) {
        setTokenStatsFetchError(true);
      }
    } finally {
      if (currentRequestRef.current?.id === id) {
        setLoadingTokenStats(false);
        currentRequestRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    return () => cancelPendingRequest();
  }, [cancelPendingRequest]);

  return {
    tokenStats,
    tokenStatsFetchError,
    loadingTokenStats,
    setTokenStats,
    fetchTokenStats,
  };
}
